import type { ReactNode } from 'react'
import cx from 'classnames'
import { m, useTransform } from 'motion/react'
import { useSpringValue } from '@/hooks/motion-hooks'
import { Icon, type IconName } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import styles from './PayoutSummary.module.css'
import { formatMoney } from '@/utils/money-util'

export interface PayoutSummaryProps {
  isPending: boolean
  earningsYearToDate: number
  pendingEarnings: number
  lifetimeEarnings: number
}

export function PayoutSummary({
  isPending,
  earningsYearToDate,
  pendingEarnings,
  lifetimeEarnings,
}: PayoutSummaryProps) {
  return (
    <div className={styles.root}>
      <Stat
        label="Year-to-date earnings"
        value={earningsYearToDate}
        icon="total-paid"
        variant="info"
        isPending={isPending}
      />
      <Stat
        label="Pending"
        value={pendingEarnings}
        icon="collect-payment"
        variant="info"
        isPending={isPending}
      />
      <Stat
        label="Lifetime earnings"
        value={lifetimeEarnings}
        icon="chart-arrow-up"
        variant="success"
        isPending={isPending}
      />
    </div>
  )
}

interface StatProps {
  label: ReactNode
  value: number
  icon: IconName
  isPending: boolean
  variant: 'info' | 'success'
}

/**
 * Stat
 */
export function Stat({
  label,
  value: valueInput,
  icon,
  variant,
  isPending,
}: StatProps) {
  const value = useSpringValue(valueInput, 0)
  const text = useTransform(value, (n) => formatMoney(n))
  return (
    <div
      className={cx(styles.stat, styles[variant], isPending && styles.loading)}
    >
      <div className={styles.statContent}>
        <div className={styles.icon}>
          <Icon name={icon} size={24} />
        </div>
        <div className={styles.label}>{label}</div>
        <m.div className={styles.value}>{text}</m.div>
        {isPending && (
          <div className={styles.spinner}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  )
}
